// Observation metadata never changes the legacy 25-column adapter contract.
export function strideObservations(rows) {
  const headers = rows?.[0] || [];
  if (!headers.includes('Shipment #') || !headers.includes('PO')) return null;
  const index = Object.fromEntries(headers.map((h, i) => [String(h).trim(), i]));
  return rows.slice(1).map((row, i) => {
    const raw = index['Load #'] === undefined ? undefined : row[index['Load #']];
    const value = raw == null ? '' : String(raw).trim();
    return { po: String(row[index.PO] ?? '').trim(), row: i + 2,
      shipmentId: String(row[index['Shipment #']] ?? '').trim(),
      ...(index['Shipment Cancelled Date/Time'] === undefined ? {} : {shipmentCancelledAt: String(row[index['Shipment Cancelled Date/Time']] ?? '').trim()}),
      state: value === '-/-' || (raw != null && value === '') ? 'UNASSIGNED' : value ? 'ASSIGNED' : 'UNKNOWN',
      loadId: value && value !== '-/-' ? value : null };
  }).filter(row => row.po);
}

// A protected source observation is kept separately; it is not a second operational candidate.
export function preserveObservedOperationalRows(incoming, previous, observations = []) {
  const poKey = incoming.headers.find(h => h.label === 'PO Number')?.key;
  const loadKey = incoming.headers.find(h => h.label === 'Load ID')?.key;
  const oldPo = previous?.headers?.find(h => h.label === 'PO Number')?.key;
  const oldLoad = previous?.headers?.find(h => h.label === 'Load ID')?.key;
  const oldCust = previous?.headers?.find(h => h.label === 'Cust Code')?.key || 'cust-code';
  const data = incoming.data.map(row => {
    const observed = observations.filter(o => String(o.po).trim() === String(row[poKey] || '').trim());
    // Only an unassigned/unknown source observation protects the old candidate.
    // Assigned changes must reach the existing merge and server import guards.
    if (observed.length !== 1 || !['UNASSIGNED', 'UNKNOWN'].includes(observed[0].state)) return row;
    const matches = (previous?.data || []).filter(old => !old.__isSuperseded &&
      String(old[oldCust] || '').trim().toUpperCase() === row['cust-code'] &&
      String(old[oldPo] || '').trim() === String(row[poKey] || '').trim());
    if (matches.length !== 1 || incoming.data.filter(x => x[poKey] === row[poKey]).length !== 1) return row; // Split-load resolution stays with the existing guard.
    const old = matches[0], load = String(old[oldLoad] || '').trim();
    if (load && load !== '0' && load !== String(row[loadKey] || '').trim()) {
      return Object.fromEntries([...incoming.headers.map(h => {
        const oldKey = previous.headers.find(x => x.label === h.label)?.key;
        return [h.key, old[oldKey] ?? ''];
      }), ['cust-code', row['cust-code']], ['__routingUid', old.__routingUid], ['__routingRowId', old.__routingRowId]]);
    }
    return row;
  });
  return {...incoming, data};
}

export async function workbookSha256(file) {
  const bytes = await file.arrayBuffer();
  return workbookBytesSha256(bytes);
}

export async function workbookBytesSha256(bytes) {
  const hash = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('');
}

export function workbookBytesBase64(bytes) {
  const view = new Uint8Array(bytes);
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < view.length; offset += chunkSize) {
    binary += String.fromCharCode(...view.subarray(offset, offset + chunkSize));
  }
  return window.btoa(binary);
}

// Retry only a network failure, with the identical serialized request and import identity.
export async function sendRoutingSave(url, options, retryImport = false, transport = fetch) {
  try { return await transport(url, options); }
  catch (error) {
    if (!retryImport || !(error instanceof TypeError)) throw error;
    return transport(url, options);
  }
}
